import cn from 'classnames';
import { forwardRef, useState } from 'react';

import { Tag } from '../tag';

import type { ForwardedRef, KeyboardEvent, MouseEvent, ReactElement } from 'react';

import type { FilterTagProps } from './filter-tag.types';

import './filter-tag.scss';

/**
 * Filter tags are interactive labels used to filter criteria.
 *
 * ### Usage
 *
 * ```tsx
 * import { FilterTag } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <FilterTag>Name</FilterTag>;
 * }
 * ```
 */
const FilterTag = forwardRef(
  (props: FilterTagProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      defaultSelected = false,
      onChange,
      selected,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const [isSelected, setIsSelected] = useState<boolean>(defaultSelected);
    const isControlled = selected !== undefined;
    const actualChecked = isControlled ? selected : isSelected;

    const handleChange = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>): void => {
      const newState = !actualChecked;
      if (!isControlled) {
        setIsSelected(newState);
      }

      onChange?.(event, newState);
    };

    const classes = cn('rbui-filter-tag', { 'rbui-filter-tag--selected': actualChecked }, className);

    return (
      <Tag
        aria-selected={actualChecked}
        className={classes}
        ref={forwardedRef}
        {...rest}
        bordered={false}
        disabled={false}
        dismissible={false}
        onClick={handleChange}
        readOnly={false}
      >
        {children}
      </Tag>
    );
  },
);

FilterTag.displayName = 'FilterTag';

export default FilterTag;
